import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  listSuperAdminOhts,
  ohtNeedsAttention,
  readinessMissingLabels,
  type SuperAdminOhtSummary,
} from "../../../../api/superAdminOhts";
import "./SuperAdminOhtsPage.css";

/**
 * Super Admin — Ontario Health Teams
 *
 * - List OHTs from GET /api/super-admin/ohts
 * - Create OHT via /portal/super-admin/ohts/create
 * - Open detail for identity/status edits
 */

type BadgeTone = "success" | "danger" | "warning" | "neutral" | "info";

export default function SuperAdminOhtsPage() {
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [rows, setRows] = useState<SuperAdminOhtSummary[]>([]);

  async function loadOhts(mode: "initial" | "refresh" = "initial") {
    if (mode === "initial") setLoading(true);
    setErr(null);

    try {
      const list = await listSuperAdminOhts();
      setRows(list);
    } catch (e: any) {
      setErr(e?.message || "Failed to load OHTs");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setErr(null);
      try {
        const list = await listSuperAdminOhts();
        if (!cancelled) setRows(list);
      } catch (e: any) {
        if (!cancelled) {
          setErr(e?.message || "Failed to load OHTs");
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const summary = useMemo(() => {
    let active = 0;
    let intakeEnabled = 0;
    let needsAttention = 0;

    for (const row of rows) {
      if (row.isActive) active += 1;
      if (row.isNavigatorIntakeEnabled) intakeEnabled += 1;
      if (ohtNeedsAttention(row)) needsAttention += 1;
    }

    return {
      total: rows.length,
      active,
      intakeEnabled,
      needsAttention,
    };
  }, [rows]);

  const openOht = (ohtId: number) => {
    nav(`/portal/super-admin/ohts/${encodeURIComponent(String(ohtId))}`);
  };

  return (
    <div className="sa-ohts">
      <div className="sa-ohts__header">
        <div className="sa-ohts__titleWrap">
          <div className="sa-ohts__eyebrow">OHT governance</div>
          <h1 className="sa-ohts__title">Ontario Health Teams</h1>
          <p className="sa-ohts__subtitle">
            Review and manage Ontario Health Team configuration, coverage and
            portal readiness.
          </p>
        </div>

        <div className="sa-ohts__headerActions">
          <button
            type="button"
            className="sa-ohts__refreshBtn"
            onClick={() => loadOhts("refresh")}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
          <button
            type="button"
            className="sa-ohts__createBtn"
            onClick={() => nav("/portal/super-admin/ohts/create")}
          >
            Create OHT
          </button>
        </div>
      </div>

      <section
        className={
          summary.needsAttention > 0
            ? "sa-ohts__summary sa-ohts__summary--attention"
            : "sa-ohts__summary"
        }
        aria-label="OHT summary"
      >
        <div className="sa-ohts__summaryRow">
          <SummaryPill label={`${summary.total} total OHTs`} tone="info" />
          <SummaryPill
            label={`${summary.active} active OHTs`}
            tone="success"
          />
          <SummaryPill
            label={`${summary.intakeEnabled} Navigator intake enabled`}
            tone="info"
          />
          <SummaryPill
            label={`${summary.needsAttention} needs attention`}
            tone={summary.needsAttention > 0 ? "warning" : "neutral"}
          />
        </div>
      </section>

      <div className={err ? "sa-ohts__card sa-ohts__card--error" : "sa-ohts__card"}>
        <div className="sa-ohts__cardHeader">
          <div className="sa-ohts__cardTitle">All OHTs</div>
          <div className="sa-ohts__count">
            {loading ? "Loading…" : `${rows.length} shown`}
          </div>
        </div>

        {err ? <div className="sa-ohts__error">{err}</div> : null}

        <div className="sa-ohts__tableWrap">
          <table className="sa-ohts__table">
            <thead>
              <tr>
                <th className="sa-ohts__th" scope="col">
                  Name
                </th>
                <th className="sa-ohts__th" scope="col">
                  Code
                </th>
                <th className="sa-ohts__th" scope="col">
                  Status
                </th>
                <th className="sa-ohts__th" scope="col">
                  FSA coverage
                </th>
                <th className="sa-ohts__th" scope="col">
                  Clinics
                </th>
                <th className="sa-ohts__th" scope="col">
                  Doctors
                </th>
                <th className="sa-ohts__th" scope="col">
                  OHT Admins
                </th>
                <th className="sa-ohts__th" scope="col">
                  Navigators
                </th>
                <th className="sa-ohts__th" scope="col">
                  Navigator intake
                </th>
                <th className="sa-ohts__th" scope="col">
                  Readiness
                </th>
                <th className="sa-ohts__th" scope="col">
                  Open
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr className="sa-ohts__tr">
                  <td className="sa-ohts__td sa-ohts__td--muted" colSpan={11}>
                    Loading OHTs...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr className="sa-ohts__tr">
                  <td className="sa-ohts__td sa-ohts__td--muted" colSpan={11}>
                    No Ontario Health Teams have been configured.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const readiness = row.navigatorIntakeReadiness;
                  const ready = readiness?.canEnable === true;
                  const missing = ready
                    ? []
                    : readinessMissingLabels(row);

                  return (
                    <tr key={row.ohtId} className="sa-ohts__tr">
                      <td className="sa-ohts__td">
                        <div className="sa-ohts__name">
                          {row.name || "—"}
                        </div>
                        <div className="sa-ohts__meta">OHT ID: {row.ohtId}</div>
                      </td>
                      <td className="sa-ohts__td">{row.code || "—"}</td>
                      <td className="sa-ohts__td">
                        <span
                          className={
                            row.isActive
                              ? "sa-ohts__status sa-ohts__status--active"
                              : "sa-ohts__status sa-ohts__status--inactive"
                          }
                        >
                          {row.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="sa-ohts__td sa-ohts__td--right">
                        {row.coverageFsaCount}
                      </td>
                      <td className="sa-ohts__td sa-ohts__td--right">
                        {row.clinicCount}
                      </td>
                      <td className="sa-ohts__td sa-ohts__td--right">
                        {row.doctorCount}
                      </td>
                      <td className="sa-ohts__td sa-ohts__td--right">
                        {row.activeOhtAdminCount}
                      </td>
                      <td className="sa-ohts__td sa-ohts__td--right">
                        {row.activeCommunityNavigatorCount}
                      </td>
                      <td className="sa-ohts__td">
                        <span
                          className={
                            row.isNavigatorIntakeEnabled
                              ? "sa-ohts__status sa-ohts__status--enabled"
                              : "sa-ohts__status sa-ohts__status--disabled"
                          }
                        >
                          {row.isNavigatorIntakeEnabled
                            ? "Enabled"
                            : "Disabled"}
                        </span>
                      </td>
                      <td className="sa-ohts__td">
                        <span
                          className={
                            ready
                              ? "sa-ohts__status sa-ohts__status--ready"
                              : "sa-ohts__status sa-ohts__status--needs"
                          }
                        >
                          {ready ? "Ready" : "Needs setup"}
                        </span>
                        {!ready && missing.length > 0 ? (
                          <div className="sa-ohts__readinessNote">
                            {missing.join(" · ")}
                          </div>
                        ) : null}
                      </td>
                      <td className="sa-ohts__td sa-ohts__td--right">
                        <button
                          type="button"
                          className="sa-ohts__rowBtn"
                          onClick={() => openOht(row.ohtId)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="sa-ohts__hint">
          Organization status and Navigator intake status are separate. This
          page is read-only.
        </div>
      </div>
    </div>
  );
}

function SummaryPill({
  label,
  tone = "neutral",
}: {
  label: string;
  tone?: BadgeTone;
}) {
  const styles: Record<
    BadgeTone,
    { bg: string; bd: string; fg: string; dot: string }
  > = {
    success: {
      bg: "rgba(22, 163, 74, 0.10)",
      bd: "rgba(22, 163, 74, 0.25)",
      fg: "#166534",
      dot: "#16a34a",
    },
    danger: {
      bg: "rgba(185, 28, 28, 0.08)",
      bd: "rgba(185, 28, 28, 0.22)",
      fg: "#991b1b",
      dot: "#dc2626",
    },
    warning: {
      bg: "rgba(217, 119, 6, 0.10)",
      bd: "rgba(217, 119, 6, 0.28)",
      fg: "#92400e",
      dot: "#d97706",
    },
    neutral: {
      bg: "rgba(100, 116, 139, 0.08)",
      bd: "rgba(148, 163, 184, 0.35)",
      fg: "#475569",
      dot: "#94a3b8",
    },
    info: {
      bg: "rgba(37, 99, 235, 0.08)",
      bd: "rgba(37, 99, 235, 0.22)",
      fg: "#1d4ed8",
      dot: "#2563eb",
    },
  };

  const s = styles[tone];

  return (
    <span
      className="sa-ohts__pill"
      style={{ background: s.bg, borderColor: s.bd, color: s.fg }}
    >
      <span className="sa-ohts__pillDot" style={{ background: s.dot }} aria-hidden />
      {label}
    </span>
  );
}
